import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDroppable } from '@dnd-kit/core';
import { Package, Check, X, ChevronLeft, ChevronRight, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import SkuProductCard from './SkuProductCard';

interface Product {
  id_catalogo: string;
  title?: string;
  sitio?: string;
  sale_price?: string | number;
  imagen?: string;
  [key: string]: any;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface SkuMetadata {
  custom_name?: string | null;
  custom_image?: string | null;
  reference_price?: number | null;
  first_product_title?: string;
  first_product_image?: string | null;
}

interface SkuProductsColumnProps {
  selectedSku: string | null;
  skuMetadata?: SkuMetadata | null;
  products: Product[];
  isLoading: boolean;
  isCreatingNewSku: boolean;
  newSkuCode: string;
  pagination?: Pagination;
  onNewSkuCodeChange: (value: string) => void;
  onConfirmNewSku: () => void;
  onCancelNewSku: () => void;
  onRemoveProduct: (id_catalogo: string) => void;
  onPageChange?: (page: number) => void;
  onEditSku?: () => void;
  onDeleteSku?: () => void;
}

const SkuProductsColumn: React.FC<SkuProductsColumnProps> = ({
  selectedSku,
  skuMetadata,
  products,
  isLoading,
  isCreatingNewSku,
  newSkuCode,
  pagination,
  onNewSkuCodeChange,
  onConfirmNewSku,
  onCancelNewSku,
  onRemoveProduct,
  onPageChange,
  onEditSku,
  onDeleteSku,
}) => {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({
    id: 'sku-products-drop-zone',
  });

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Format price for display
  const formatPrice = (price: number | null | undefined) => {
    if (price === null || price === undefined) return null;
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const displayName = skuMetadata?.custom_name || selectedSku;
  const displayImage = skuMetadata?.custom_image || skuMetadata?.first_product_image;
  const referencePrice = skuMetadata?.reference_price;

  return (
    <div className="flex flex-col h-full bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-3 border-b border-gray-200 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <Package className="h-4 w-4" />
          {t('skus.sku_products_header')}
        </h3>

        {/* SKU identifier or new SKU input */}
        {isCreatingNewSku ? (
          <div className="mt-2 flex items-center gap-2">
            <input
              type="text"
              value={newSkuCode}
              onChange={(e) => onNewSkuCodeChange(e.target.value)}
              placeholder={t('skus.new_sku_placeholder')}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              autoFocus
            />
            <button
              onClick={onConfirmNewSku}
              disabled={!newSkuCode.trim()}
              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('skus.confirm')}
            >
              <Check className="h-5 w-5" />
            </button>
            <button
              onClick={onCancelNewSku}
              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title={t('skus.cancel')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        ) : selectedSku ? (
          <div className="mt-2">
            {/* SKU Name and Menu Row */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {/* SKU Image thumbnail */}
                {displayImage && (
                  <div className="flex-shrink-0 w-8 h-8 rounded overflow-hidden bg-gray-100">
                    <img
                      src={displayImage}
                      alt=""
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <span className="text-lg font-bold text-pink-600 truncate" title={displayName || selectedSku}>
                  {displayName}
                </span>
              </div>

              {/* Menu button */}
              <div className="relative flex-shrink-0" ref={menuRef}>
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title={t('skus.menu.options')}
                >
                  <MoreVertical className="h-5 w-5" />
                </button>

                {/* Dropdown menu */}
                {isMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        onEditSku?.();
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Pencil className="h-4 w-4" />
                      {t('skus.menu.edit')}
                    </button>
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        onDeleteSku?.();
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      {t('skus.menu.delete')}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* SKU ID (if different from name) and Reference Price Row */}
            <div className="flex items-center justify-between mt-1">
              <div className="flex items-center gap-2">
                {skuMetadata?.custom_name && skuMetadata.custom_name !== selectedSku && (
                  <span className="text-xs text-gray-500">SKU: {selectedSku}</span>
                )}
                {referencePrice && (
                  <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded">
                    {t('skus.reference_price')}: {formatPrice(referencePrice)}
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-500">
                {pagination?.total || products.length} {(pagination?.total || products.length) === 1 ? t('skus.product_singular') : t('skus.product_plural')}
              </span>
            </div>
          </div>
        ) : (
          <p className="mt-2 text-sm text-gray-500">
            {t('skus.select_or_create_sku')}
          </p>
        )}
      </div>

      {/* Products List / Drop Zone */}
      <div
        ref={setNodeRef}
        className={`
          flex-1 min-h-0 overflow-y-auto p-3 transition-colors
          ${isOver ? 'bg-pink-50 border-2 border-dashed border-pink-300' : ''}
          ${!selectedSku && !isCreatingNewSku ? 'bg-gray-50' : ''}
        `}
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pink-500"></div>
          </div>
        ) : !selectedSku && !isCreatingNewSku ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <Package className="h-12 w-12 mb-2" />
            <p className="text-sm text-center">
              {t('skus.select_sku_from_list')}<br />{t('skus.or_create_new')}
            </p>
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 border-2 border-dashed border-gray-300 rounded-lg p-8">
            <Package className="h-12 w-12 mb-2" />
            <p className="text-sm text-center mb-2">
              {isCreatingNewSku
                ? t('skus.drag_products_new_sku')
                : t('skus.no_products_in_sku')
              }
            </p>
            <p className="text-xs text-gray-400">
              {t('skus.drag_from_search')}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {products.map((product) => (
              <SkuProductCard
                key={product.id_catalogo}
                product={product}
                onRemove={onRemoveProduct}
                showRemoveButton
                referencePrice={referencePrice}
              />
            ))}

            {/* Drop indicator at bottom */}
            {isOver && (
              <div className="border-2 border-dashed border-pink-400 rounded-lg p-4 text-center text-pink-500 text-sm">
                {t('skus.drop_to_add')}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="px-3 py-2 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          {(() => {
            const start = (pagination.page - 1) * pagination.limit + 1;
            const end = pagination.total
              ? Math.min(pagination.page * pagination.limit, pagination.total)
              : pagination.page * pagination.limit;
            const total = pagination.total ?? '—';
            return (
              <span className="text-[11px] text-gray-500">
                {start} {t('skus.pagination.from')} {end} {t('skus.pagination.of')} {total} · {t('skus.pagination.page')} {pagination.page} {t('skus.pagination.of_pages')} {pagination.totalPages}
              </span>
            );
          })()}
          <button
            onClick={() => onPageChange?.(pagination.page - 1)}
            disabled={pagination.page <= 1}
            className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => onPageChange?.(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages}
            className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Footer hint */}
      {(selectedSku || isCreatingNewSku) && (!pagination || pagination.totalPages <= 1) && (
        <div className="p-2 border-t border-gray-200 bg-gray-50 text-center text-xs text-gray-500">
          {t('skus.drag_hint')}
        </div>
      )}
    </div>
  );
};

export default SkuProductsColumn;
